// inspired by /packages/photon/src/molecules/checkbox/index.tsx
import { Checkbox as CheckboxAtom } from '@lichtblick/photon/atoms/checkbox';
import { FieldFooter } from '@lichtblick/photon/atoms/field-footer';
import { Text } from '@lichtblick/photon/atoms/text/text';
import classNames from 'classnames';
import type { FC, ReactElement } from 'react';
import styles from './checkbox-card.module.css';

export interface CheckboxCardOptionProps {
  children: ReactElement<typeof CheckboxCard.Checkbox>;
  footer?: ReactElement | string;
  label: ReactElement | string;
  dataTestId?: string;
}

const CheckboxCardOption: FC<CheckboxCardOptionProps> = ({ children, label, footer, dataTestId }) => (
  /* biome-ignore lint/a11y/noLabelWithoutControl: part of children */
  <label className={classNames(styles.label)} data-testid={dataTestId}>
    {children}
    <Text className={styles.text} size="l" bold>
      {label}
    </Text>
    {Boolean(footer) && <FieldFooter data-testid="field-footer">{footer}</FieldFooter>}
  </label>
);

interface CheckboxCardComponent extends FC<CheckboxCardProps> {
  Checkbox: typeof CheckboxAtom;
  Footer: typeof FieldFooter;
  Option: typeof CheckboxCardOption;
}
export interface CheckboxCardProps {
  className?: string;
  'data-testid'?: string;
  forcedError?: boolean;
  children: ReactElement<typeof CheckboxCardOption>[] | ReactElement<typeof CheckboxCardOption>;
}
/**
 * `CheckboxCard` groups one or more `CheckboxCard.Option` items into a visually
 * connected card with shared border styling and optional error state.
 *
 * @property {ReactElement<typeof CheckboxCardOption> | ReactElement<typeof CheckboxCardOption>[]} children - One or more `CheckboxCard.Option` elements.
 * @property {string} [data-testid] - Test ID forwarded to the wrapper element.
 * @property {boolean} [forcedError] - Forces the visual error state (red borders and checkbox outlines) without relying on `:user-invalid`.
 *
 * @example
 * ```tsx
 * <CheckboxCard forcedError={hasError}>
 *   <CheckboxCard.Option
 *     label="Solaranlage"
 *     footer="mit / ohne Batteriespeicher"
 *   >
 *    <CheckboxCard.Checkbox
 *      checked={isChecked}
 *      onChange={handleChange}
 *      name="interestInPv"
 *      value="true"
 *    />
 *  </CheckboxCard.Option>
 * </CheckboxCard>
 * ```
 */
export const CheckboxCard: CheckboxCardComponent = ({ children, 'data-testid': dataTestId, forcedError }) => (
  <div className={classNames(styles.wrapper, forcedError && styles['forced-error'])} data-testid={dataTestId}>
    {children}
  </div>
);

CheckboxCard.Footer = FieldFooter;
CheckboxCard.Checkbox = CheckboxAtom;
CheckboxCard.Option = CheckboxCardOption;
